import { useCallback, useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { fmtMoney, todayStr, thisMonth } from "@/lib/format";
import { PrintableStatement } from "@/components/PrintableStatement";
import { SettlementSlipBody, buildSettlementShareText } from "@/components/SettlementSlip";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash, Wallet, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Payments() {
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [empId, setEmpId] = useState("");
  const [start, setStart] = useState(`${thisMonth()}-01`);
  const [end, setEnd] = useState(todayStr());
  const [data, setData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [bucket, setBucket] = useState("regular");
  const [showSlip, setShowSlip] = useState(false);

  const loadEmployees = () => api.get("/employees").then((r) => setEmployees(r.data));
  const loadPayments = () => api.get("/payments").then((r) => setPayments(r.data));
  useEffect(() => { loadEmployees(); loadPayments(); }, []);

  const loadSettlement = useCallback(() => {
    if (!empId) { setData(null); return; }
    api.get(`/employees/${empId}/settlement?start=${start}&end=${end}`).then((r) => {
      setData(r.data);
      setAmount(r.data.balance > 0 ? String(r.data.balance) : "");
    });
  }, [empId, start, end]);
  useEffect(() => { loadSettlement(); }, [loadSettlement]);

  const chooseBucket = (b) => {
    setBucket(b);
    if (!data) return;
    const bal = b === "arecanut" ? data.arecanut.balance : data.balance;
    setAmount(bal > 0 ? String(bal) : "");
  };

  const record = async () => {
    if (!empId) { toast.error("Select a labourer"); return; }
    if (!amount || Number(amount) <= 0) { toast.error("Enter a valid amount"); return; }
    await api.post("/payments", {
      employee_id: empId, date: end, amount: Number(amount), bucket,
      note: note || `Settlement ${start} to ${end}`,
    });
    toast.success("Payment recorded");
    setNote("");
    loadSettlement(); loadPayments();
  };

  const remove = async (id) => { await api.delete(`/payments/${id}`); toast.success("Payment deleted"); loadSettlement(); loadPayments(); };

  const empName = employees.find((e) => e.id === empId)?.name;

  return (
    <div className="p-4 md:p-8 max-w-[1100px] mx-auto w-full space-y-6">
      <PageHeader title="Payments & Settlement" subtitle="Pick a date range to see days worked, wages and lends, then settle." />

      {/* Settle card */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Labourer</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="settle-employee"><SelectValue placeholder="Select labourer" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>From</Label>
            <Input data-testid="settle-start" type="date" className="font-data" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>To</Label>
            <Input data-testid="settle-end" type="date" className="font-data" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
        </div>

        {!empId && <p className="text-sm text-slate-400">Select a labourer to see their settlement summary.</p>}

        {data && (() => {
          const view = bucket === "arecanut"
            ? {
                shift_rows: data.arecanut.shift_rows, absent: 0, earned: data.arecanut.earned_in_range,
                previous: data.arecanut.previous_balance,
                lend: data.arecanut.lend_in_range, advance: data.arecanut.advance_in_range,
                paid: data.arecanut.paid_in_range, balance: data.arecanut.balance,
              }
            : {
                shift_rows: data.shift_rows, absent: data.absent, earned: data.earned_in_range,
                previous: data.previous_balance,
                lend: data.lend_in_range, advance: 0,
                paid: data.paid_in_range, balance: data.balance,
              };
          return (
          <div className="space-y-3 pt-2">
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
              <button data-testid="settle-bucket-regular" onClick={() => chooseBucket("regular")}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${bucket === "regular" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Regular</button>
              <button data-testid="settle-bucket-arecanut" onClick={() => chooseBucket("arecanut")}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${bucket === "arecanut" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Arecanut</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Attendance breakdown */}
            <div className="rounded-md border border-slate-200 overflow-hidden">
              <div className="px-4 py-2 bg-slate-100 text-xs font-semibold uppercase tracking-widest text-slate-500">Attendance in range</div>
              <table className="w-full text-sm">
                <tbody className="font-data">
                  {view.shift_rows.map((r) => (
                    <tr key={r.shift_id} className="border-b border-slate-100" data-testid={`settle-shift-${r.shift_id}`}>
                      <td className="px-4 py-2 text-slate-600">{r.name}</td>
                      <td className="px-4 py-2 text-center text-slate-500">{r.count} days</td>
                      <td className="px-4 py-2 text-right">{fmtMoney(r.subtotal)}</td>
                    </tr>
                  ))}
                  {view.shift_rows.length === 0 && <tr><td className="px-4 py-3 text-slate-400 text-center" colSpan={3}>No {bucket === "arecanut" ? "arecanut-season" : ""} shifts worked in this range.</td></tr>}
                  {view.absent > 0 && <tr className="border-b border-slate-100"><td className="px-4 py-2 text-rose-600">Absent</td><td className="px-4 py-2 text-center text-slate-500">{view.absent} days</td><td className="px-4 py-2 text-right">—</td></tr>}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-data font-semibold border-t border-slate-200">
                    <td className="px-4 py-2" colSpan={2}>Earned</td>
                    <td className="px-4 py-2 text-right" data-testid="settle-earned">{fmtMoney(view.earned)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Money summary */}
            <div className="rounded-md border border-slate-200 bg-slate-900 text-white p-5">
              <div className="grid grid-cols-2 gap-y-2 font-data text-sm">
                <span className="text-slate-400">Previous balance</span><span className={`text-right ${view.previous < 0 ? "text-rose-400" : ""}`}>{fmtMoney(view.previous)}</span>
                <span className="text-slate-400">+ Earned (range)</span><span className="text-right">{fmtMoney(view.earned)}</span>
                {bucket === "arecanut" && (
                  <>
                    <span className="text-slate-400">− Advances (range)</span><span className="text-right text-orange-300">{fmtMoney(view.advance)}</span>
                  </>
                )}
                <span className="text-slate-400">− Lends (range)</span><span className="text-right text-orange-300">{fmtMoney(view.lend)}</span>
                <span className="text-slate-400">− Already paid (range)</span><span className="text-right">{fmtMoney(view.paid)}</span>
              </div>
              <div className="border-t border-slate-700 mt-3 pt-3 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-slate-400">Balance to pay</span>
                <span className={`font-data text-xl font-semibold ${view.balance < 0 ? "text-rose-400" : "text-emerald-400"}`} data-testid="settle-balance">{fmtMoney(view.balance)}</span>
              </div>
              <div className="flex gap-2 mt-4">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-data text-sm">₹</span>
                  <Input data-testid="settle-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="pl-7 bg-slate-800 border-slate-700 text-white font-data" placeholder="Amount to pay" />
                </div>
                <Button data-testid="btn-record-payment" onClick={record} className="bg-emerald-500 hover:bg-emerald-600 gap-2"><Wallet size={16} weight="bold" /> Pay</Button>
              </div>
              <Button data-testid="btn-settle-export" variant="outline" onClick={() => setShowSlip(true)}
                className="w-full mt-2 bg-transparent border-slate-600 text-white hover:bg-slate-800 gap-2"><FileText size={16} /> Export / Share Slip</Button>
            </div>
            </div>
          </div>
          );
        })()}
      </div>

      {/* Payment history */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-widest text-slate-500">Payment History</div>
        <table className="w-full text-sm" data-testid="payments-table">
          <thead>
            <tr className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-left px-4 py-3 font-semibold">Labourer</th>
              <th className="text-center px-4 py-3 font-semibold">Bucket</th>
              <th className="text-right px-4 py-3 font-semibold">Amount</th>
              <th className="text-left px-4 py-3 font-semibold">Note</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-data text-slate-600">{p.date}</td>
                <td className="px-4 py-3 font-medium text-slate-800">{p.employee_name}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${p.bucket === "arecanut" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                    {p.bucket === "arecanut" ? "Arecanut" : "Regular"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-data text-emerald-700 font-medium">
                  {p.kind === "clear"
                    ? <span className="text-amber-700" title="Account cleared by hand">{p.amount < 0 ? `Received ${fmtMoney(-p.amount)}` : fmtMoney(p.amount)}</span>
                    : fmtMoney(p.amount)}
                </td>
                <td className="px-4 py-3 text-slate-500">{p.note}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`delete-payment-${p.id}`} aria-label="Delete payment" onClick={() => setConfirmDelete({
                    title: p.kind === "clear" ? "Undo this account clear?" : "Delete this payment?",
                    description: `${p.employee_name} · ${p.date} · ${p.amount < 0 ? `received ${fmtMoney(-p.amount)}` : fmtMoney(p.amount)}${p.note ? ` · ${p.note}` : ""}. `
                      + (p.kind === "clear" ? "Their balance goes back to what it was before clearing." : "Their balance goes back up by this amount."),
                    confirmLabel: p.kind === "clear" ? "Undo Clear" : "Delete",
                    onConfirm: () => remove(p.id),
                  })}
                    className="h-8 w-8 flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 ml-auto"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
            {payments.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No payments recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {data && (
        <PrintableStatement open={showSlip} onClose={() => setShowSlip(false)} testid="settle-slip"
          shareText={() => buildSettlementShareText(empName, data)}
          onExcel={() => downloadFile(
            `/employees/${empId}/settlement/excel?start=${start}&end=${end}`,
            `settlement_${(empName || "labourer").replace(/\s+/g, "_")}_${start}_to_${end}.xlsx`
          )} excelLabel="Export Excel"
          pdfFileName={`settlement_${(empName || "labourer").replace(/\s+/g, "_")}_${start}_to_${end}.pdf`}>
          <SettlementSlipBody name={empName} data={data} />
        </PrintableStatement>
      )}
      <ConfirmDelete target={confirmDelete} onClose={() => setConfirmDelete(null)} />
    </div>
  );
}